import { Box } from '@material-ui/core';
import { Exception, SchemaErrorException } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { ErrorMessageContact } from '@/components/shared/components/ErrorBoundary/components/ErrorMessage/components/ErrorMessageContact';
import { ErrorMessagePaper } from '@/components/shared/components/ErrorBoundary/components/ErrorMessage/components/ErrorMessagePaper';
import { useErrorBoundary } from '@/components/shared/components/ErrorBoundary/hooks/useErrorBoundary';
import { useGetSupportContactData } from '@/components/shared/components/ErrorBoundary/hooks/useGetSupportContactData';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';

interface ErrorMessageProps {
  error?: Error | Exception | SchemaErrorException;
  isAsync?: boolean;
  retryFn?: () => void | Promise<void>;
  clearErrorBoundary?: () => void;
}

export const ErrorMessage = ({ error, isAsync, retryFn, clearErrorBoundary }: ErrorMessageProps) => {
  const { opened, setOpened } = useDialog();
  const [loading, setLoading] = useState(false);
  const { supportPhone, supportEmail } = useGetSupportContactData();
  const { tryCatchAndRetryInDialog } = useErrorBoundary();

  useEffect(() => {
    if (error) {
      setOpened(true);
    }
  }, [error, setOpened]);

  if (!error || !supportEmail || !supportPhone) return null;

  const onClose = async () => {
    setOpened(false);
    clearErrorBoundary && clearErrorBoundary();
  };

  const onRetry = async () => {
    if (!retryFn) return;

    setLoading(true);
    await tryCatchAndRetryInDialog(retryFn);

    // setTimeout to see loader in UI and avoid flickering
    setTimeout(() => {
      setOpened(false);
      setLoading(false);
    }, 500);
  };

  const onReload = async () => {
    window.location.reload();
  };

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          {isAsync && (
            <StandardButton color={'secondary'} onClick={onClose}>
              <Text strong tId={'CLOSE'} />
            </StandardButton>
          )}
          {retryFn && (
            <StandardButton variant={'outlined'} color={'secondary'} onClick={onRetry} loading={loading}>
              <Text strong tId={'RETRY'} />
            </StandardButton>
          )}
          {!retryFn && !isAsync && (
            <StandardButton variant={'outlined'} color={'secondary'} onClick={onReload} loading={loading}>
              <Text tId={'RELOAD_PAGE'} />
            </StandardButton>
          )}
        </>
      }
      dialogProps={{ maxWidth: 'md' }}
    >
      <Box display={'flex'} gridGap={'24px'} flexDirection={'column'}>
        <Font variant="h3">
          <Text tId={'ERROR'} />
        </Font>
        <Font variant="body2">
          <Text tId={isAsync ? 'ERROR_BOUNDARY_TEXT_ASYNC' : 'ERROR_BOUNDARY_TEXT'} />
        </Font>
        <ErrorMessagePaper error={error} />
        <ErrorMessageContact supportPhone={supportPhone} supportEmail={supportEmail} />
      </Box>
    </NemuruDialog>
  );
};
